<template>
  <div>
    <div v-if="loading" class="dw-loading">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else-if="loadError" class="dw-empty">Could not load coaching tickets.</div>
    <template v-else>
      <!-- the workable states only: a signed-off ticket is history, and this
           card is a queue. escalated is a subset of the queue rather than a
           status of its own, which is why it sits beside the two statuses
           instead of among them — it is the one that should pull the eye. -->
      <div class="dw-chip-row">
        <button
          v-for="chip in chips"
          :key="chip.key"
          type="button"
          class="dw-chip"
          :class="[chip.tone, { 'dw-chip--zero': !chip.count }]"
          :style="chip.status ? null : { cursor: 'default' }"
          :title="chip.title"
          @click="goToList(chip.status)"
        >{{ chip.label }}<span class="dw-chip-count">{{ chip.count }}</span></button>
      </div>

      <div v-if="!rows.length" class="dw-empty">
        Nothing needs coaching right now.
      </div>
      <div v-else class="dw-table-wrap">
        <table class="dw-table">
          <thead>
            <tr>
              <th>Driver</th>
              <th>Topic</th>
              <th>Due</th>
            </tr>
          </thead>
          <tbody>
            <!-- the row wears its urgency as a left edge in the same hue the
                 chips use: escalated red, merely past due amber -->
            <tr
              v-for="row in rows"
              :key="row.id"
              :class="row.rowClass"
              :title="row.tip"
              @click="goToTicket(row.id)"
            >
              <td>{{ row.driver }}</td>
              <td class="dw-cell-wrap" :title="row.topicFull">
                <span class="dw-clamp-3">{{ row.topic }}</span>
              </td>
              <td>{{ row.due }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { get_coaching_tickets } from '../../networking/apis'
import { OPEN_STATUSES } from '../../utils/coaching_labels'

const MAX_ROWS = 5

export default {
  name: 'widget_coaching_portal',
  props: {
    tenant: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      tickets: [],
      summary: {},
    }
  },
  computed: {
    // coaching only: recognition is praise, and it must never make the
    // roster look like it has more coaching problems (the same rule the
    // summary counts follow server-side)
    coachingTickets() {
      return this.tickets.filter(t => t.kind !== 'recognition')
    },
    queue() {
      return this.coachingTickets.filter(t => OPEN_STATUSES.includes(t.status))
    },
    // server-side, over the whole queue — not recounted from the rows,
    // which are only the working set
    escalatedCount() {
      return this.summary.escalated || 0
    },
    chips() {
      const open = this.summary.open || 0
      const scheduled = this.summary.scheduled || 0
      return [
        {
          key: 'open', label: 'Open', status: 'open', count: open,
          tone: 'dw-chip-danger',
          title: 'Raised, not yet handled — across the fleet',
        },
        {
          key: 'scheduled', label: 'Scheduled', status: 'scheduled', count: scheduled,
          tone: 'dw-chip-warning',
          title: 'The conversation is booked in',
        },
        {
          // the tickets page has no escalated filter, so this one reports
          // rather than promising a view it cannot open
          key: 'escalated', label: 'Escalated', status: null, count: this.escalatedCount,
          tone: 'dw-chip-danger',
          title: 'Overdue past your fleet’s escalation threshold',
        },
      ]
    },
    // the queue in the order somebody should work it: escalated, then merely
    // past due, then whatever is due soonest. a plain "newest first" list
    // would bury the tickets that are actually late.
    rows() {
      const rank = t => (t.escalated ? 0 : t.past_due ? 1 : 2)
      return [...this.queue]
        .sort((a, b) => rank(a) - rank(b)
          || String(a.due_date || '9999').localeCompare(String(b.due_date || '9999')))
        .slice(0, MAX_ROWS)
        .map(t => ({
          id: t.id,
          driver: t.driver_name || '—',
          topic: t.topic || '—',
          topicFull: t.why || t.topic || '',
          due: this.dueLabel(t),
          rowClass: t.escalated ? 'dw-row-danger' : (t.past_due ? 'dw-row-warning' : 'dw-row-neutral'),
          tip: t.escalated ? 'Escalated' : (t.past_due ? t.days_overdue + 'd overdue' : 'Due ' + this.dueLabel(t)),
        }))
    },
  },
  created() {
    this.load()
  },
  methods: {
    dueLabel(t) {
      if (t.past_due) return t.days_overdue + 'd late'
      return t.due_date ? moment(t.due_date.slice(0, 10)).format('MMM D') : '—'
    },
    // tenant rides on every deep link: ticket ids are per-tenant counters,
    // so a bare id can resolve against the wrong fleet if the acting tenant
    // ever diverges (the tickets page toasts about exactly that case).
    // assignee=everyone because these counts are fleet-wide — without it the
    // page auto-scopes to Mine and a chip reading 12 lands on a list of 3.
    goToList(status) {
      if (!status) return
      this.$router.push({
        name: 'coaching_portal',
        query: { tenant: this.tenant, status, assignee: 'everyone' },
      }).catch(() => {})
    },
    // ?ticket_id= opens that ticket's sign-off record directly
    goToTicket(id) {
      this.$router.push({
        name: 'coaching_portal',
        query: { tenant: this.tenant, ticket_id: id },
      }).catch(() => {})
    },
    async load() {
      this.loading = true
      this.loadError = false
      try {
        // one call, and only the working set: list_tickets computes the
        // summary over the WHOLE queue before applying filters, so asking for
        // just the open+scheduled coaching rows keeps the chip counts honest
        // while sparing a home page the tenant's entire ticket history.
        // deliberately not the flagged-driver numbers either — those need a
        // second, much heavier scorecard fetch.
        const { value: { data = {} } = {} } = await get_coaching_tickets(
          this.tenant, { status: 'open,scheduled', kind: 'coaching' })
        this.tickets = data.tickets || []
        this.summary = data.summary || {}
      } catch (e) {
        this.loadError = true
      }
      this.loading = false
    },
  },
}
</script>
